import { ApiHolder } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

import {
  catalogNameTakenMessage,
  findCatalogComponentByRepositoryName,
  getEntityVcsProvider,
  githubOrgFromFieldValue,
  githubRepoTakenMessage,
} from './catalogNameUniqueness';
import { githubOrgsApiRef } from '../../utils/GithubOrgsApi';

const DEFAULT_GITHUB_ORG_FIELD = 'github_org';
const DEFAULT_GITHUB_HOST = 'github.com';

type ValidatorContext = {
  apiHolder: ApiHolder;
  formData?: Record<string, unknown>;
  uiSchema?: { 'ui:options'?: Record<string, unknown> };
};

/**
 * The GitHub org the new repository targets, or undefined when the form is not
 * creating a GitHub repository. The org field name defaults to `github_org`
 * and can be overridden with `ui:options.githubOrgField`.
 */
function resolveGithubTarget(
  context: ValidatorContext,
): { host: string; org: string } | undefined {
  const formData = context.formData ?? {};
  const options = context.uiSchema?.['ui:options'] ?? {};

  const vcsProvider = formData.vcs_provider;
  if (typeof vcsProvider === 'string' && vcsProvider !== 'github') {
    return undefined;
  }

  const orgField =
    (options.githubOrgField as string | undefined) || DEFAULT_GITHUB_ORG_FIELD;
  const org = githubOrgFromFieldValue(formData[orgField]);
  if (!org) {
    return undefined;
  }

  const host =
    (options.githubHost as string | undefined) || DEFAULT_GITHUB_HOST;
  return { host, org };
}

async function checkCatalog(
  repositoryName: string,
  validation: { addError: (message: string) => void },
  apiHolder: ApiHolder,
): Promise<void> {
  const catalogApi = apiHolder.get(catalogApiRef);
  if (!catalogApi) {
    return;
  }

  try {
    const existing = await findCatalogComponentByRepositoryName(
      catalogApi,
      repositoryName,
    );
    if (!existing) {
      return;
    }

    validation.addError(
      catalogNameTakenMessage(
        repositoryName.toLowerCase(),
        getEntityVcsProvider(existing),
      ),
    );
  } catch {
    // Do not block Create if the catalog is unavailable.
  }
}

async function checkGithubOrg(
  repositoryName: string,
  validation: { addError: (message: string) => void },
  context: ValidatorContext,
): Promise<void> {
  const target = resolveGithubTarget(context);
  if (!target) {
    return;
  }

  const githubOrgsApi = context.apiHolder.get(githubOrgsApiRef);
  if (!githubOrgsApi) {
    return;
  }

  try {
    const exists = await githubOrgsApi.repoExists(
      target.host,
      target.org,
      repositoryName,
    );
    if (exists) {
      validation.addError(githubRepoTakenMessage(repositoryName, target.org));
    }
  } catch {
    // Do not block Create if the GitHub check is unavailable.
  }
}

export async function validateRepositoryNameUniqueness(
  value: string,
  validation: { addError: (message: string) => void },
  context: ValidatorContext,
): Promise<void> {
  const repositoryName = (value ?? '').trim();
  if (!repositoryName) {
    return;
  }

  await Promise.all([
    checkCatalog(repositoryName, validation, context.apiHolder),
    checkGithubOrg(repositoryName, validation, context),
  ]);
}
